import { Button, Card, Checkbox, MenuItem, Select, Stack, Typography, styled } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { useCallback, useEffect, useState } from "react";
import { produce } from "immer";
import { useTranslation } from "react-i18next";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";

export type BasketCopyConfigWithContentsChoice = {
  itemId: number;
  contentId: number;
  checked: boolean;
};

export type BasketCopyConfigWithContentsProps = {
  srcItem: DbOrderBasketItemInfos;
  copiedItems: DbOrderBasketItemInfos[];
  onConfirm: (choice: BasketCopyConfigWithContentsChoice[]) => void;
  onCancel: () => void;
};

const BasketCopyConfigWithContents: React.FC<BasketCopyConfigWithContentsProps> = (props) => {
  const { t } = useTranslation();
  const [choices, setChoices] = useState<BasketCopyConfigWithContentsChoice[]>([]);

  const isContentSelected = useCallback(
    (itemId: number): boolean => {
      return choices.filter((x) => x.itemId === itemId && x.checked).length === 1;
    },
    [choices]
  );

  const handleContentCheckbox = useCallback(
    (itemId: number) => {
      setChoices(
        produce((draft) => {
          draft.find((x) => x.itemId === itemId)!.checked = !draft.find((x) => x.itemId === itemId)!.checked;
        })
      );
    },
    [choices]
  );

  useEffect(() => {
    let defaultChoices: BasketCopyConfigWithContentsChoice[] = [];

    for (let i = 0; i < props.copiedItems.length; ++i) {
      if (
        props.copiedItems[i].assetInfos === null ||
        props.copiedItems[i].assetInfos?.childObjects === null ||
        props.copiedItems[i].assetInfos?.childObjects?.length === 0
      )
        continue;

      let matchContent = props.copiedItems[i].assetInfos?.childObjects?.find(
        (x) => x.name === props.srcItem.selectedContent?.name
      )?.id;

      if (!matchContent)
        matchContent = props.copiedItems[i].assetInfos?.childObjects?.find(
          (x) =>
            GetMetadataByTagName(x, "contentType")?.value ===
            GetMetadataByTagName(props.srcItem.selectedContent, "contentType")?.value
        )?.id;

      if (!matchContent) matchContent = props.copiedItems[i].assetInfos!.childObjects![0].id;

      defaultChoices.push({
        itemId: props.copiedItems[i].id,
        contentId: matchContent,
        checked: true,
      });
    }

    setChoices(defaultChoices);
  }, [props.srcItem, props.copiedItems]);

  return (
    <Stack justifyContent="center" alignItems="center" textAlign="center" height={"100%"}>
      <Typography variant="h5" component="h2">
        {t("page:body.copyConfig")}
      </Typography>
      <Typography mt={1} mb={2} component="p" color="text.secondary">
        {t("page:body.selectItemsWherePasteConfigAndContentChoices")}
      </Typography>

      {choices.map((row) => (
        <StyledCard variant="outlined" sx={{ mt: 1 }} key={row.itemId}>
          <Stack justifyContent="space-between" direction="row" sx={{ width: "100%" }}>
            <Stack direction="row" spacing={1} textAlign="center" alignItems="center">
              <Checkbox checked={isContentSelected(row.itemId)} onChange={() => handleContentCheckbox(row.itemId)} />
              <Typography fontWeight={500}>{props.copiedItems.find((x) => x.id === row.itemId)?.name}</Typography>
            </Stack>
            <Select
              IconComponent={ExpandMoreRoundedIcon}
              inputProps={{ "aria-label": "Without label" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              value={choices.find((x) => x.itemId === row.itemId)!.contentId}
              onChange={(e, v) =>
                setChoices(
                  produce((draft) => {
                    draft.find((x) => x.itemId === row.itemId)!.contentId = e.target.value as number;
                  })
                )
              }
            >
              {props.copiedItems
                .find((x) => x.id === row.itemId)
                ?.assetInfos?.childObjects?.filter((x) => x.layer === ObjectLayer.Content)
                ?.map((content) => (
                  <MenuItem value={content.id}>{content.name}</MenuItem>
                ))}
            </Select>
          </Stack>
        </StyledCard>
      ))}

      <Stack direction="row" mt={3} spacing={1}>
        <Button
          variant="contained"
          endIcon={<CopyAllIcon />}
          onClick={() => props.onConfirm(choices)}
          disabled={choices.filter((x) => x.checked).length === 0}
        >
          {t("button:confirmCopy")}
        </Button>
        <Button variant="contained" color="inherit" onClick={props.onCancel}>
          {t("common:cancel")}
        </Button>
      </Stack>
    </Stack>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  display: "flex",
  padding: `10px 10px 10px 10px`,
  borderRadius: 6,
  transition: "all .3s ease-out",
}));

export default BasketCopyConfigWithContents;
